import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  CNAME_TARGET,
  PUBLISH_TARGETS,
  normalizeDomain,
  validateCustomDomain,
} from "../../data/publishing";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";
import { visibleControls } from "../../data/controls";
import { describeChanges } from "../../data/publishing";

const TargetOption = ({ value, selected, onSelect, title, children }) => {
  const theme = useTheme();
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-label={title}
      tabIndex={0}
      onClick={() => onSelect(value)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(value)}
      sx={{
        p: 2,
        cursor: "pointer",
        borderRadius: "12px",
        border: `1px solid ${
          selected
            ? theme.customColors.icons.primary
            : theme.customColors.border.main
        }`,
        backgroundColor: selected
          ? theme.customColors.status.info.bg
          : theme.customColors.background.main,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Radio
          checked={selected}
          size="small"
          tabIndex={-1}
          sx={{ p: 0, mt: 0.25 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          {children}
        </Box>
      </Stack>
    </Box>
  );
};

// `target`: what the CNAME points at (the API's value; the build setting
// until it has loaded).
export const DnsRecord = ({ domain, target = CNAME_TARGET }) => {
  const theme = useTheme();
  const cell = {
    fontFamily: "monospace",
    fontSize: 13,
    wordBreak: "break-all",
  };
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${theme.customColors.border.main}`,
        backgroundColor: theme.customColors.background.infoBox,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        Add this record where you manage your DNS (GoDaddy, Cloudflare, Route
        53…):
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          columnGap: 2,
          rowGap: 0.5,
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Type
        </Typography>
        <Typography sx={cell}>CNAME</Typography>
        <span />
        <Typography variant="caption" color="text.secondary">
          Name
        </Typography>
        <Typography sx={cell}>{domain || "trust.yourcompany.com"}</Typography>
        <span />
        <Typography variant="caption" color="text.secondary">
          Points to
        </Typography>
        <Typography sx={cell}>{target}</Typography>
        <Tooltip title="Copy" arrow>
          <IconButton
            size="small"
            aria-label="Copy CNAME target"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard?.writeText(target);
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          color: theme.customColors.text.secondary,
        }}
      >
        DNS changes can take up to 24 hours. We set up HTTPS for you once the
        record is found.
      </Typography>
    </Box>
  );
};

// What "Publish changes" will put live: one line per section.
const ChangeList = ({ changes, previewLink }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${theme.customColors.border.main}`,
        backgroundColor: theme.customColors.background.infoBox,
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
        What buyers will see change
      </Typography>
      {changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing has changed since you last published.
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Changes to publish">
          {describeChanges(changes).map((line) => (
            <Typography component="li" variant="body2" key={line}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
      {previewLink && (
        <Link
          href={previewLink}
          target="_blank"
          rel="noopener"
          variant="body2"
          underline="hover"
          sx={{ display: "inline-block", mt: 1 }}
        >
          Open Preview to check them first
        </Link>
      )}
    </Box>
  );
};

// Shown every time the admin clicks Publish: choose where the page lives
// (first publish), or see what changes (already published), then
// acknowledge what publishing means before the button turns on.
const PublishDialog = ({
  open,
  data,
  regoditLink,
  previewLink,
  onPublish,
  onClose,
}) => {
  const theme = useTheme();
  const [target, setTarget] = useState("regodit");
  const [customDomain, setCustomDomain] = useState("");
  const [domainError, setDomainError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget(data.publishing?.target || "regodit");
      setCustomDomain(data.publishing?.customDomain || "");
      setDomainError("");
      // Never remembered: the admin acknowledges on every publish.
      setAcknowledged(false);
    }
  }, [open, data.publishing]);

  const domain = normalizeDomain(customDomain);

  const handlePublish = () => {
    if (target === "custom") {
      const problem = validateCustomDomain(
        customDomain,
        data.company?.domain || ""
      );
      if (problem) {
        setDomainError(problem);
        return;
      }
    }
    onPublish({
      target,
      customDomain: target === "custom" ? domain : "",
      acknowledgedAt: new Date().toISOString(),
    });
    onClose();
  };
  // Already live: where it lives is changed under Settings → Custom domain.
  const republish = !!data.isPublished;

  const statements = [
    `I am allowed to publish security information on behalf of ${data.company.name}.`,
    "The overview, resources and subprocessors are correct, and contain no evidence, personal data, passwords or other secrets.",
    // Follows the Controls tab setting: failing controls may be hidden.
    visibleControls(
      data.controls || [],
      data.controlVisibility,
      data.frameworks
    ).some(
      (c) => c.status === "fail"
    )
      ? "Visitors see live control status, including controls that are failing."
      : "Visitors see your controls as set on the Controls tab.",
    "The page is public. Requestable documents still need your approval on Access requests before visitors can open them.",
    ...(target === "custom"
      ? [
          `My company owns ${domain || "this domain"}, and I will add the DNS record above.`,
        ]
      : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title={republish ? "Publish changes" : "Publish your Trust Center"}
        onClose={onClose}
      />
      <DialogContent dividers>
        {republish ? (
          <ChangeList
            changes={data.draft?.changes || []}
            previewLink={previewLink}
          />
        ) : (
          <>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Where should it live?
            </Typography>
            <Stack
              spacing={1.5}
              role="radiogroup"
              aria-label="Where to publish"
            >
              <TargetOption
                value="regodit"
                selected={target === "regodit"}
                onSelect={setTarget}
                title={PUBLISH_TARGETS.regodit.label}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    wordBreak: "break-all",
                  }}
                >
                  Live right away at {regoditLink}
                </Typography>
              </TargetOption>
              <TargetOption
                value="custom"
                selected={target === "custom"}
                onSelect={setTarget}
                title={PUBLISH_TARGETS.custom.label}
              >
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  Use a subdomain you own, like trust.yourcompany.com.
                </Typography>
                {target === "custom" && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      label="Your domain"
                      placeholder="trust.yourcompany.com"
                      value={customDomain}
                      onChange={(e) => {
                        setCustomDomain(e.target.value);
                        setDomainError("");
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      error={!!domainError}
                      helperText={domainError}
                      sx={{ mt: 1.5, backgroundColor: "#FFFFFF" }}
                    />
                    <DnsRecord
                      domain={domain}
                      target={data.publishing?.cnameTarget}
                    />
                  </Box>
                )}
              </TargetOption>
            </Stack>
          </>
        )}

        <Alert
          severity="warning"
          icon={false}
          sx={{
            mt: 3,
            borderRadius: "12px",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Before you publish
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {statements.map((statement) => (
              <Typography
                component="li"
                variant="body2"
                key={statement}
                sx={{ mb: 0.5 }}
              >
                {statement}
              </Typography>
            ))}
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                I have read this and agree
              </Typography>
            }
          />
          <Typography
            variant="caption"
            sx={{ display: "block", color: theme.customColors.text.secondary }}
          >
            We save your name and the time with this agreement.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!acknowledged}
          onClick={handlePublish}
          sx={primaryButtonSx(theme)}
        >
          {republish ? "Publish changes" : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishDialog;
